import z from 'zod'
import { zRemoteFileWithUrl } from './fileWithUrl'
import { zLayoutNode } from './layoutNode'
import { zColor } from './color'
import { zAspectRatio } from '../../util/aspectRatio'

export const zThumbnailCanvas = z.object({
  nodes: z.array(zLayoutNode),
  file: zRemoteFileWithUrl,
  aspectRatio: z.custom<string>((v) => typeof v === 'string' && new RegExp(/^\d+:\d+$/).test(v)),
  backgroundColor: zColor.optional(),
  /** Video timestamp (ms) the thumbnail was captured from. Used by TikTok
   *  which only supports frame-based covers, not image uploads. */
  sourceTimestampMs: z.number().optional(),
})
export type ThumbnailCanvas = z.infer<typeof zThumbnailCanvas>

/** Media-level AI-provenance metadata. Drives the default value of
 *  platform-level AI-disclosure flags (TikTok `is_aigc`, YouTube
 *  `status.containsSyntheticMedia`). Both fields are optional for backwards
 *  compatibility with existing JSONB rows — missing values are treated as
 *  `origin: 'user_upload'` / `hasSyntheticEdits: false` at read time. Use the
 *  `requiresAiDisclosure` helper instead of reading these directly. */
const zProvenance = {
  origin: z.enum(['user_upload', 'ai_generated']).optional(),
  hasSyntheticEdits: z.boolean().optional(),
}

export const zWorkspaceMedia = z.discriminatedUnion('type', [
  z.object({ type: z.literal('image'), file: zRemoteFileWithUrl, ...zProvenance }),
  z.object({ type: z.literal('video'), file: zRemoteFileWithUrl, ...zProvenance }),
  z.object({ type: z.literal('document'), file: zRemoteFileWithUrl, ...zProvenance }),
  z.object({
    type: z.literal('canvas'),
    nodes: z.array(zLayoutNode),
    file: zRemoteFileWithUrl,
    aspectRatio: zAspectRatio,
    backgroundColor: zColor.optional(),
    ...zProvenance,
  }),
])
export type WorkspaceMedia = z.infer<typeof zWorkspaceMedia>

/** Resolves whether a media item requires AI-disclosure on platforms that
 *  enforce it. Treats missing provenance as user-uploaded / non-synthetic. */
export const requiresAiDisclosure = (m: Pick<WorkspaceMedia, 'origin' | 'hasSyntheticEdits'>) =>
  m.origin === 'ai_generated' || m.hasSyntheticEdits === true

/** Both provenance fields, resolved. */
export type AiProvenance = {
  origin: 'user_upload' | 'ai_generated'
  hasSyntheticEdits: boolean
}

/**
 * Provenance for an asset an AI operation just EDITED.
 *
 * Use this at every such site rather than writing the pair by hand. Provenance
 * was previously set independently at five places and four were wrong, each
 * differently: two dropped both fields (so AI-edited media read as a plain
 * upload, suppressing the disclosure and the IPTC marker), two overwrote `origin`
 * with `ai_generated` (claiming an edited photograph was entirely synthetic), and
 * one never wrote them at all. Every one of those passed its tests, because the
 * resolvers reading these fields were never the broken part.
 *
 * The rule the helper encodes: an edit changes what HAPPENED to an asset, not
 * what the asset WAS. So `origin` is carried forward and only
 * `hasSyntheticEdits` is set. That is what keeps an edited photograph
 * distinguishable from an edited generation — both arrive with
 * `hasSyntheticEdits: true`, and `origin` is the only thing separating them.
 *
 * `hasSyntheticEdits` means AI edited, NOT edited. Do not call this for a manual
 * change: marking a hand-cropped photograph would claim AI involvement in work
 * that had none.
 */
export const markAiEdited = (
  source: Pick<WorkspaceMedia, 'origin' | 'hasSyntheticEdits'> | undefined,
): AiProvenance => ({
  origin: source?.origin ?? 'user_upload',
  hasSyntheticEdits: true,
})

/** Provenance for an asset AI created from nothing. Distinct from
 *  `markAiEdited` because there is no prior asset whose origin survives. */
export const markAiGenerated = (): AiProvenance => ({
  origin: 'ai_generated',
  hasSyntheticEdits: false,
})
